import {
  ACTIONS, pathParams, type ActionName, type ApiResponse, type ArgsOf, type ResultOf,
} from '@hbac/contract';
import type { Transport } from './transport';

/**
 * Talks to the Worker API.
 *
 * Same origin as the SPA (one Worker serves both), so there is no CORS and no
 * `Content-Type: text/plain` workaround — that existed only because Apps
 * Script could not answer a preflight.
 *
 * The session token goes in `Authorization: Bearer`, so no screen has to know
 * where in an argument list a particular endpoint expects it.
 */
export class HttpTransport implements Transport {
  readonly name = 'http';

  constructor(
    private readonly baseUrl = '/api',
    private readonly getToken: () => string | null = () => null,
  ) {}

  async call<A extends ActionName>(action: A, args: ArgsOf<A>): Promise<ResultOf<A>> {
    const spec = ACTIONS[action];
    const params = pathParams(spec.path);

    // Leading arguments fill the :params, in declaration order.
    let path: string = spec.path;
    params.forEach((name, i) => {
      path = path.replace(`:${name}`, encodeURIComponent(String((args as unknown[])[i])));
    });

    const rest = spec.args.slice(params.length);
    const restValues = (args as unknown[]).slice(params.length);

    let url = `${this.baseUrl}${path}`;
    const init: RequestInit = { method: spec.method, headers: {} };
    const headers = init.headers as Record<string, string>;

    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    if (rest.length > 0) {
      if (spec.method === 'GET') {
        url += `?args=${encodeURIComponent(JSON.stringify(restValues))}`;
      } else {
        // Named body keys rather than a positional array: the payload stays
        // readable in devtools and in server logs. Files (Blob, File) are taken
        // out and sent as multipart parts (Plan AM): a third lighter than base64
        // in JSON, and nothing to encode or decode on either side.
        const blobs: Blob[] = [];
        const json = JSON.stringify(Object.fromEntries(rest.map((key, i) => [key, restValues[i]])), (_key, value: unknown) =>
          value instanceof Blob ? { $blob: blobs.push(value) - 1 } : value,
        );
        if (blobs.length > 0) {
          const form = new FormData();
          form.append('json', json);
          blobs.forEach((b, i) => form.append(`blob${i}`, b, b instanceof File ? b.name : `file${i}`));
          init.body = form;
        } else {
          headers['Content-Type'] = 'application/json';
          init.body = json;
          // A small write still reaches the server if the tab closes mid-request
          // (e.g. the check-in screen saying it has closed). Browsers cap keepalive
          // bodies at 64 KB in flight, so large ones go without.
          if (json.length < 60_000) init.keepalive = true;
        }
      }
    }

    let res: Response;
    try {
      res = await fetch(url, init);
    } catch (cause) {
      throw new Error(`Không kết nối được máy chủ (${action}).`, { cause });
    }

    let payload: ApiResponse<ResultOf<A>>;
    try {
      payload = (await res.json()) as ApiResponse<ResultOf<A>>;
    } catch {
      throw new Error(`Máy chủ trả về dữ liệu không hợp lệ (${res.status}).`);
    }

    if (!payload.success) throw new Error(payload.error);
    return payload.data;
  }
}
